import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../api/axiosConfig";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CssBaseline,
  FormControlLabel,
  Grow,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { ThemeProvider, styled } from "@mui/material/styles";

// Icons
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import theme from "../../theme";
import Lottie from "lottie-react";
import successAnimationData from "../../assets/icons/sucess.json";
import heroImage from "../../assets/web/feature1.jpg";

import * as C from "../../constants";
import { getStoredRoles, isUserAuthenticated, persistAuthSession } from "../../utils/authSession";
import { getDefaultAuthorizedPath } from "../../utils/roleAccess";
import {
  clearSelectedLoginSchool,
  readSelectedLoginSchool,
  saveSelectedLoginSchool,
} from "./loginSchoolSelection";

// --- Configuration ---
const LOGIN_TYPE_OPTIONS =[
  { value: "employee", label: "Employee", authType: "employee" },
  { value: "student", label: "Parent", authType: "student" },
];

// --- Styled Components ---

const PageShell = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  padding: 0,
  background: "linear-gradient(180deg, #eef3ff 0%, #f7f9ff 100%)",
  position: "relative",
  overflow: "hidden",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  color: "#161616",
  [theme.breakpoints.down("md")]: {
    padding: 0,
  },
}));

const WorkspaceFrame = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: "100vh",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.2fr) minmax(500px, 590px)",
  borderRadius: 0,
  overflow: "hidden",
  background: "#ffffff",
  border: "none",
  boxShadow: "none",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    minHeight: "100vh",
    borderRadius: 0,
  },
}));

const HeroPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  justifyContent: "space-between",
  backgroundImage: `linear-gradient(180deg, rgba(6, 11, 25, 0.18) 0%, rgba(6, 11, 25, 0.58) 100%), url(${heroImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: theme.spacing(4, 4, 4.5),
  [theme.breakpoints.down("md")]: {
    minHeight: 340,
    padding: theme.spacing(3, 2.5, 3),
  },
}));

const HeroBrand = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.2),
  color: "#ffffff",
}));

const HeroBadgeRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(1.2),
  marginTop: theme.spacing(2.2),
}));

const HeroBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.8),
  color: "rgba(255,255,255,0.96)",
  fontSize: "0.88rem",
  fontWeight: 600,
}));

const MainCanvas = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: theme.spacing(5, 3, 2.5),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.75, 1.25, 2),
  },
}));

const AuthCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 620,
  borderRadius: 6,
  padding: theme.spacing(1, 0),
  background: "transparent",
  border: "none",
  boxShadow: "none",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    padding: 0,
    borderRadius: 0,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputLabel-root": {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#64748b",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 6,
    backgroundColor: "#ffffff",
    fontSize: "0.94rem",
    minHeight: 56,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#ffffff",
    },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
    },
    "& fieldset": {
      borderColor: "rgba(203, 213, 225, 0.95)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(148, 163, 184, 1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.9),
    },
  },
  "& .MuiOutlinedInput-input": {
    paddingTop: 16,
    paddingBottom: 16,
  },
  "& .MuiInputBase-input::placeholder": {
    fontSize: "0.92rem",
    opacity: 0.9,
  },
}));

// --- Main Component ---

const LoginPage = ({ handleLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Consolidated Form State - UserType defaults to "employee"
  const [form, setForm] = useState({
    schoolCode: "",
    userType: "employee",
    userId: "",
    password: "",
    rememberMe: false,
  });

  const [uiState, setUiState] = useState({
    isLoading: false,
    message: "",
    messageType: "error", // 'error' | 'success'
    showPassword: false,
    loginSuccessAnimation: false,
  });
  const [authConfig, setAuthConfig] = useState({
    loading: false,
    loginMode: "PASSWORD",
    passwordAllowed: true,
    otpAllowed: false,
    mobileLoginEnabled: false,
    parentMobileLoginEnabled: false,
  });
  const [loginMethod, setLoginMethod] = useState("password");
  const [otpState, setOtpState] = useState({
    otp: "",
    requested: false,
    maskedPhone: "",
    candidates: [],
    selectedStudentId: "",
  });
  const [selectedSchool, setSelectedSchool] = useState(null);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateUi = (updates) => setUiState((prev) => ({ ...prev, ...updates }));
  const updateOtpState = (updates) => setOtpState((prev) => ({ ...prev, ...updates }));

  // Dynamic derivations
  const selectedLoginType = LOGIN_TYPE_OPTIONS.find((opt) => opt.value === form.userType) || LOGIN_TYPE_OPTIONS[0];
  const isStudentLogin = selectedLoginType.authType === "student";
  const canUseParentMobile = isStudentLogin && loginMethod === "otp" && authConfig.otpAllowed && authConfig.parentMobileLoginEnabled;
  const userIdLabel = isStudentLogin ? "MOBILE NUMBER" : "MOBILE NUMBER";
  const userIdPlaceholder = isStudentLogin ? "Enter mobile number" : "Enter mobile number";
  
  // Initialization Effects
  useEffect(() => {
    if (isUserAuthenticated()) {
      navigate(getDefaultAuthorizedPath(getStoredRoles()) || C.ROUTES.DASHBOARD, { replace: true });
      return;
    }

    const savedSchool = readSelectedLoginSchool();
    const registeredFlag = searchParams.get("registered");
    if (registeredFlag === "1") {
      const schoolName = searchParams.get("school");
      const schoolCode = searchParams.get("code");
      
      updateUi({
        messageType: "success",
        message: `Registration complete${schoolName ? ` for ${schoolName}` : ""}. Credentials sent via email.${schoolCode ? ` School code: ${schoolCode}.` : ""}`,
      });
      if (schoolCode) {
        updateForm("schoolCode", schoolCode);
      }
    }

    if (savedSchool) {
      setSelectedSchool(savedSchool);
      updateForm("schoolCode", savedSchool.schoolCode);
    } else if (!searchParams.get("code")) {
      navigate("/select-school", { replace: true });
      return;
    }

    // Load Remembered Credentials with Failsafe check
    if (localStorage.getItem("rememberMe") === "1") {
      const savedUserType = localStorage.getItem("rememberUserType") || "employee";
      const isValidUserType = LOGIN_TYPE_OPTIONS.some((opt) => opt.value === savedUserType);

      setForm((prev) => ({
        ...prev,
        rememberMe: true,
        schoolCode: savedSchool?.schoolCode || localStorage.getItem("rememberSchoolCode") || "",
        userType: isValidUserType ? savedUserType : "employee",
        userId: localStorage.getItem("rememberUserId") || "",
      }));
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!form.schoolCode?.trim() || selectedSchool?.schoolCode?.toUpperCase() === form.schoolCode.trim().toUpperCase()) {
      return;
    }

    let active = true;
    const loadSchoolByCode = async () => {
      try {
        const response = await axiosInstance.get("/auth/schools/search", {
          params: { q: form.schoolCode.trim().toUpperCase() },
          skipAuthHeaders: true,
          skipAuthRedirect: true,
        });
        if (!active) return;
        const match = Array.isArray(response.data)
          ? response.data.find((item) => item?.schoolCode?.toUpperCase() === form.schoolCode.trim().toUpperCase())
          : null;
        if (match) {
          setSelectedSchool(match);
          saveSelectedLoginSchool(match);
        }
      } catch (error) {
        // Ignore hydration failures and let the user search again.
      }
    };

    loadSchoolByCode();
    return () => {
      active = false;
    };
  }, [form.schoolCode, selectedSchool]);

  useEffect(() => {
    setOtpState({
      otp: "",
      requested: false,
      maskedPhone: "",
      candidates: [],
      selectedStudentId: "",
    });
  }, [form.schoolCode, form.userType, form.userId]);

  useEffect(() => {
    const schoolCode = form.schoolCode.trim();
    if (!schoolCode || !selectedLoginType?.authType) {
      setAuthConfig({
        loading: false,
        loginMode: "PASSWORD",
        passwordAllowed: true,
        otpAllowed: false,
        mobileLoginEnabled: false,
        parentMobileLoginEnabled: false,
      });
      setLoginMethod("password");
      return;
    }

    let active = true;
    const loadLoginOptions = async () => {
      setAuthConfig((prev) => ({ ...prev, loading: true }));
      try {
        const response = await axiosInstance.get("/auth/login-options", {
          params: {
            schoolCode: schoolCode.toUpperCase(),
            userType: selectedLoginType.authType,
          },
          skipAuthHeaders: true,
          skipAuthRedirect: true,
        });
        if (!active) return;
        const nextConfig = {
          loading: false,
          loginMode: response.data?.loginMode || "PASSWORD",
          passwordAllowed: !!response.data?.passwordAllowed,
          otpAllowed: !!response.data?.otpAllowed,
          mobileLoginEnabled: !!response.data?.mobileLoginEnabled,
          parentMobileLoginEnabled: !!response.data?.parentMobileLoginEnabled,
        };
        setAuthConfig(nextConfig);
        setLoginMethod((current) => {
          if (nextConfig.passwordAllowed && nextConfig.otpAllowed) {
            return current === "otp" ? "otp" : "password";
          }
          if (nextConfig.otpAllowed) return "otp";
          return "password";
        });
      } catch (error) {
        if (!active) return;
        setAuthConfig({
          loading: false,
          loginMode: "PASSWORD",
          passwordAllowed: true,
          otpAllowed: false,
          mobileLoginEnabled: false,
          parentMobileLoginEnabled: false,
        });
        setLoginMethod("password");
      }
    };

    loadLoginOptions();
    return () => {
      active = false;
    };
  }, [form.schoolCode, selectedLoginType?.authType]);

  // Handlers
  const handleLoginError = (msg) => {
    updateUi({ message: msg || C.UI_TEXT.ERROR_GENERIC, messageType: "error" });
  };

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school || null);
    setOtpState({
      otp: "",
      requested: false,
      maskedPhone: "",
      candidates: [],
      selectedStudentId: "",
    });

    if (school?.schoolCode) {
      updateForm("schoolCode", school.schoolCode);
      saveSelectedLoginSchool(school);
    } else {
      updateForm("schoolCode", "");
      clearSelectedLoginSchool();
    }
  };

  const handleLoginSuccess = (data) => {
    // 1. Validate required fields cleanly
    const requiredKeys =[
      C.STORAGE_KEYS.AUTH_TOKEN, C.STORAGE_KEYS.SCHOOL_ID, C.STORAGE_KEYS.USER_TYPE,
      C.STORAGE_KEYS.USER_CODE, C.STORAGE_KEYS.USER_ID, C.STORAGE_KEYS.FIRST_NAME,
      C.STORAGE_KEYS.USER_ROLE,
    ];
    
    const missingFields = requiredKeys.filter((key) => !data?.[key]);
    if (missingFields.length > 0) {
      return handleLoginError(`${C.UI_TEXT.ERROR_SERVER_RESPONSE} (Missing: ${missingFields.join(", ")})`);
    }

    // 2. Map and Batch Save to LocalStorage
    const storageData = {[C.STORAGE_KEYS.AUTH_TOKEN]: data[C.STORAGE_KEYS.AUTH_TOKEN],[C.STORAGE_KEYS.USER_TYPE]: data[C.STORAGE_KEYS.USER_TYPE],[C.STORAGE_KEYS.USER_CODE]: data[C.STORAGE_KEYS.USER_CODE],[C.STORAGE_KEYS.USER_ID]: data[C.STORAGE_KEYS.USER_ID],[C.STORAGE_KEYS.FIRST_NAME]: data[C.STORAGE_KEYS.FIRST_NAME],[C.STORAGE_KEYS.LAST_NAME]: data[C.STORAGE_KEYS.LAST_NAME] || "",[C.STORAGE_KEYS.EMAIL]: data[C.STORAGE_KEYS.EMAIL] || "",[C.STORAGE_KEYS.PHONE]: data[C.STORAGE_KEYS.PHONE] || "",[C.STORAGE_KEYS.IMAGE_URL]: data[C.STORAGE_KEYS.IMAGE_URL] || "",[C.STORAGE_KEYS.USER_ROLE]: data[C.STORAGE_KEYS.USER_ROLE],
      [C.STORAGE_KEYS.DESIGNATION]: data[C.STORAGE_KEYS.DESIGNATION] || "",
      [C.STORAGE_KEYS.SCHOOL_ID]: data[C.STORAGE_KEYS.SCHOOL_ID],
      [C.STORAGE_KEYS.SCHOOL_NAME]: data[C.STORAGE_KEYS.SCHOOL_NAME] || "",
      [C.STORAGE_KEYS.SCHOOL_LOGO]: data[C.STORAGE_KEYS.SCHOOL_LOGO] || "",
      [C.STORAGE_KEYS.SCHOOL_CODE]: data[C.STORAGE_KEYS.SCHOOL_CODE] || "",[C.STORAGE_KEYS.SCHOOL_ACADEMIC_YEAR]: data[C.STORAGE_KEYS.SCHOOL_ACADEMIC_YEAR] || "",[C.STORAGE_KEYS.PRINCIPAL_SIGNATURE_URL]: data[C.STORAGE_KEYS.PRINCIPAL_SIGNATURE_URL] || "",
      [C.STORAGE_KEYS.DIRECTOR_SIGNATURE_URL]: data[C.STORAGE_KEYS.DIRECTOR_SIGNATURE_URL] || "",[C.STORAGE_KEYS.SCHOOL_SEAL_URL]: data[C.STORAGE_KEYS.SCHOOL_SEAL_URL] || "",
      [C.STORAGE_KEYS.SCHOOL_SLOGAN]: data[C.STORAGE_KEYS.SCHOOL_SLOGAN] || "",
      [C.STORAGE_KEYS.SCHOOL_ADDRESS]: data[C.STORAGE_KEYS.SCHOOL_ADDRESS] || "",[C.STORAGE_KEYS.SCHOOL_PHONE]: data[C.STORAGE_KEYS.SCHOOL_PHONE] || "",[C.STORAGE_KEYS.SCHOOL_EMAIL]: data[C.STORAGE_KEYS.SCHOOL_EMAIL] || "",[C.STORAGE_KEYS.SCHOOL_AFFILIATION_BOARD]: data[C.STORAGE_KEYS.SCHOOL_AFFILIATION_BOARD] || "",
      [C.STORAGE_KEYS.PAYMENT_DUE_DATE]: data[C.STORAGE_KEYS.PAYMENT_DUE_DATE] || "",
      [C.STORAGE_KEYS.BILLING_STATUS]: data[C.STORAGE_KEYS.BILLING_STATUS] || "",
      [C.STORAGE_KEYS.GRACE_DAYS_REMAINING]: data[C.STORAGE_KEYS.GRACE_DAYS_REMAINING] || "",
      [C.STORAGE_KEYS.REQUIRES_PAYMENT]: data[C.STORAGE_KEYS.REQUIRES_PAYMENT] || "",
      [C.STORAGE_KEYS.MESSAGE_CREDIT_BALANCE]: data[C.STORAGE_KEYS.MESSAGE_CREDIT_BALANCE] || "0",
    };

    Object.entries(storageData).forEach(([key, val]) => localStorage.setItem(key, val));

    // 3. Handle 'Remember Me'
    if (form.rememberMe) {
      localStorage.setItem("rememberMe", "1");
      localStorage.setItem("rememberSchoolCode", form.schoolCode.trim().toUpperCase());
      localStorage.setItem("rememberUserType", form.userType.trim());
      localStorage.setItem("rememberUserId", form.userId.trim());
    } else {
      ["rememberMe", "rememberSchoolCode", "rememberUserType", "rememberUserId"].forEach((k) =>
        localStorage.removeItem(k)
      );
    }

    // 4. Finalize Auth state
    persistAuthSession(data);
    if (handleLogin) handleLogin(data);

    updateUi({ loginSuccessAnimation: true });

    // 5. Redirect Logic
    setTimeout(() => {
      const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
      const isValidHost =["app.skhool.in", "app.43.205.103.60.nip.io", "skhool.in", "www.skhool.in", "localhost"].some(
        (h) => hostname === h || hostname.startsWith("127.") || hostname.endsWith(".local")
      );

      if (!isValidHost) return navigate("/", { replace: true });

      const roles = String(data[C.STORAGE_KEYS.USER_ROLE]).toUpperCase().split(",").map((r) => r.trim());
      navigate(getDefaultAuthorizedPath(roles) || C.ROUTES.DASHBOARD, { replace: true });
    }, C.CONFIG.SUCCESS_ANIMATION_DELAY);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loginMethod === "otp") {
      return;
    }
    if (!form.schoolCode.trim() || !form.userType.trim() || !form.userId.trim() || !form.password.trim()) {
      return handleLoginError(C.UI_TEXT.ERROR_MISSING_FIELDS);
    }

    updateUi({ isLoading: true, message: "" });

    try {
      const response = await axiosInstance.post(C.API.ENDPOINT, {[C.API.FIELD_USER_TYPE]: selectedLoginType.authType,[C.API.FIELD_TYPE]: form.userType === "employee" ? "school_admin" : form.userType,
        [C.API.FIELD_USER_ID]: form.userId.trim(),[C.API.FIELD_SCHOOL_CODE]: form.schoolCode.trim().toUpperCase(),[C.API.FIELD_PASSWORD]: form.password.trim(),
      }, { skipAuthHeaders: true, skipAuthRedirect: true });

      if (response.status === 200 && response.data) {
        handleLoginSuccess(response.data);
      } else {
        handleLoginError(response.data?.message || C.UI_TEXT.ERROR_SERVER_RESPONSE);
      }
    } catch (err) {
      handleLoginError(err.response?.data?.message || (err.request ? C.UI_TEXT.ERROR_NETWORK : C.UI_TEXT.ERROR_GENERIC));
    } finally {
      updateUi({ isLoading: false });
    }
  };

  const handleSendOtp = async () => {
    if (!form.schoolCode.trim() || !form.userType.trim() || !form.userId.trim()) {
      return handleLoginError("Please enter school code and user ID first.");
    }
    updateUi({ isLoading: true, message: "" });
    try {
      const response = await axiosInstance.post(
        "/auth/passwordless/request",
        {
          userType: selectedLoginType.authType,
          type: form.userType,
          userId: form.userId.trim(),
          schoolCode: form.schoolCode.trim().toUpperCase(),
        },
        { skipAuthHeaders: true, skipAuthRedirect: true }
      );
      updateOtpState({
        requested: true,
        otp: "",
        maskedPhone: response.data?.maskedPhone || "",
        candidates: Array.isArray(response.data?.candidates) ? response.data.candidates : [],
        selectedStudentId:
          Array.isArray(response.data?.candidates) && response.data.candidates.length === 1
            ? String(response.data.candidates[0].studentId)
            : "",
      });
      updateUi({
        messageType: "success",
        message: response.data?.message || "OTP sent successfully.",
      });
    } catch (err) {
      handleLoginError(err?.response?.data?.message || "Unable to send OTP.");
    } finally {
      updateUi({ isLoading: false });
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpState.otp.trim()) {
      return handleLoginError("Please enter the OTP.");
    }
    if (otpState.candidates.length > 1 && !otpState.selectedStudentId) {
      return handleLoginError("Please choose the student account.");
    }
    updateUi({ isLoading: true, message: "" });
    try {
      const response = await axiosInstance.post(
        "/auth/passwordless/verify",
        {
          userType: selectedLoginType.authType,
          type: form.userType,
          userId: form.userId.trim(),
          schoolCode: form.schoolCode.trim().toUpperCase(),
          otp: otpState.otp.trim(),
          ...(otpState.selectedStudentId ? { selectedStudentId: otpState.selectedStudentId } : {}),
        },
        { skipAuthHeaders: true, skipAuthRedirect: true }
      );

      if (response.data?.requiresSelection) {
        updateOtpState({
          candidates: Array.isArray(response.data?.candidates) ? response.data.candidates : [],
        });
        return handleLoginError(response.data?.message || "Please choose the student account.");
      }

      if (response.status === 200 && response.data?.token) {
        handleLoginSuccess(response.data);
      } else {
        handleLoginError(response.data?.message || C.UI_TEXT.ERROR_SERVER_RESPONSE);
      }
    } catch (err) {
      handleLoginError(err?.response?.data?.message || "OTP verification failed.");
    } finally {
      updateUi({ isLoading: false });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Full Screen Lottie Success Overlay */}
      {uiState.loginSuccessAnimation && (
        <Box
          sx={{
            position: "fixed", inset: 0, backgroundColor: "rgba(255,255,255,0.9)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Lottie animationData={successAnimationData} loop={false} style={{ width: 320, height: 320 }} />
        </Box>
      )}

      <PageShell>
        <Grow in timeout={800}>
          <WorkspaceFrame>
            <HeroPanel>
              <HeroBrand>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2.2,
                    backgroundColor: "rgba(255,255,255,0.14)",
                    backdropFilter: "blur(8px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.14)",
                  }}
                >
                  <SchoolRoundedIcon sx={{ fontSize: 20, color: "#ffffff" }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", letterSpacing: "-0.02em" }}>
                    Skhool
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.84rem" }}>
                    School management platform
                  </Typography>
                </Box>
              </HeroBrand>

              <Box sx={{ maxWidth: 560, mt: "auto" }}>
                <Typography
                  sx={{
                    color: "#ffffff",
                    fontSize: { xs: "2rem", md: "2.8rem" },
                    fontWeight: 800,
                    lineHeight: 1.08,
                    letterSpacing: "-0.04em",
                    maxWidth: 520,
                  }}
                >
                  Simple school operations for admins, staff, and parents.
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "1rem",
                    lineHeight: 1.7,
                    mt: 1.75,
                    maxWidth: 500,
                  }}
                >
                  Sign in to manage attendance, fees, exams, certificates, and daily school work from one place.
                </Typography>
                <HeroBadgeRow>
                  <HeroBadge>
                    <SecurityRoundedIcon sx={{ fontSize: 18 }} />
                    Protected access
                  </HeroBadge>
                  <HeroBadge>
                    <SpeedRoundedIcon sx={{ fontSize: 18 }} />
                    Fast daily workflows
                  </HeroBadge>
                  <HeroBadge>
                    <PhoneIphoneRoundedIcon sx={{ fontSize: 18 }} />
                    Mobile-ready login
                  </HeroBadge>
                </HeroBadgeRow>
              </Box>
            </HeroPanel>

            <MainCanvas>
              <AuthCard elevation={0}>
                <Box sx={{ maxWidth: 430, width: "100%", margin: "0 auto" }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <SchoolRoundedIcon sx={{ fontSize: 22, color: theme.palette.primary.main }} />
                  </Box>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.96rem", mb: 1.25, fontWeight: 500 }}>
                    Welcome to <Box component="span" sx={{ fontWeight: 700, color: "#1f2937" }}>Skhool</Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: "#111827",
                      fontSize: { xs: "1.92rem", md: "2.18rem" },
                      lineHeight: 1.14,
                      letterSpacing: "-0.04em",
                      mb: 1.4,
                    }}
                  >
                    Get started with your school account
                  </Typography>
                  <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", lineHeight: 1.65, mb: 3.25 }}>
                    Sign in with your school, user ID, and password or OTP.
                  </Typography>

                      <Box sx={{ mb: 3.5 }}>
                        <Stack direction="row" spacing={1}>
                          {LOGIN_TYPE_OPTIONS.map((option) => {
                            const active = form.userType === option.value;
                            return (
                              <Button
                                key={option.value}
                                type="button"
                                variant={active ? "contained" : "outlined"}
                                onClick={() => updateForm("userType", option.value)}
                                sx={{
                                  minWidth: 124,
                                  px: 2,
                                  py: 0.95,
                                  borderRadius: 6,
                                  textTransform: "none",
                                  fontWeight: 700,
                                  fontSize: "0.94rem",
                                  boxShadow: "none",
                                  color: active ? "#ffffff" : "#2b2b31",
                                  borderColor: active ? "transparent" : "rgba(203, 213, 225, 1)",
                                  background: active ? "linear-gradient(135deg, #059669 0%, #10b981 100%)" : "#ffffff",
                                  "&:hover": {
                                    boxShadow: "none",
                                    borderColor: active ? "transparent" : "rgba(148, 163, 184, 1)",
                                    background: active ? "linear-gradient(135deg, #047857 0%, #059669 100%)" : "#f8fafc",
                                  },
                                }}
                              >
                                {option.label}
                              </Button>
                            );
                          })}
                        </Stack>
                      </Box>

                      {uiState.message && (
                        <Alert
                          severity={uiState.messageType}
                          sx={{ mb: 3, borderRadius: 1.5, fontWeight: 600 }}
                          onClose={() => updateUi({ message: "" })}
                        >
                          {uiState.message}
                        </Alert>
                      )}

                      <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
                  <Stack spacing={2.9}>
                    
                    {/* School */}
                    <Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          px: 1.75,
                          py: 1.5,
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2.5,
                          backgroundColor: alpha(theme.palette.primary.main, 0.03),
                          borderColor: alpha(theme.palette.primary.main, 0.18),
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 1.5,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              flexShrink: 0,
                            }}
                          >
                            <SchoolRoundedIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, color: "#0f172a" }} noWrap>
                              {selectedSchool?.schoolName || selectedSchool?.name || "Selected school"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              School selected for this device
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<EditRoundedIcon fontSize="small" />}
                          onClick={() => {
                            handleSchoolSelect(null);
                            navigate("/select-school", { replace: true });
                          }}
                          sx={{ textTransform: "none", flexShrink: 0 }}
                        >
                          Change
                        </Button>
                      </Paper>
                    </Box>

                    {/* User ID */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2.25}>
                      <Box sx={{ flex: 1 }}>
                        <StyledTextField
                          fullWidth
                          size="small"
                          label={userIdLabel}
                          placeholder={userIdPlaceholder}
                          value={form.userId}
                          autoComplete="off"
                          onChange={(e) => updateForm("userId", e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {form.userType === "employee" || form.userType === "student" || authConfig.mobileLoginEnabled ? (
                                  <PhoneIphoneRoundedIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                                ) : (
                                  <BadgeRoundedIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Stack>

                    {authConfig.passwordAllowed && authConfig.otpAllowed && (
                      <Stack direction="row" spacing={1.25}>
                          <Button
                            variant={loginMethod === "password" ? "contained" : "outlined"}
                            onClick={() => setLoginMethod("password")}
                            sx={{
                              textTransform: "none",
                              borderRadius: 1.5,
                              flex: 1,
                              boxShadow: "none",
                              ...(loginMethod === "password" ? {
                                background: "linear-gradient(135deg, #4f6df6 0%, #3b5bdb 100%)",
                              } : {}),
                            }}
                          >
                            Password
                          </Button>
                          <Button
                            variant={loginMethod === "otp" ? "contained" : "outlined"}
                            onClick={() => setLoginMethod("otp")}
                            sx={{
                              textTransform: "none",
                              borderRadius: 1.5,
                              flex: 1,
                              boxShadow: "none",
                              ...(loginMethod === "otp" ? {
                                background: "linear-gradient(135deg, #4f6df6 0%, #3b5bdb 100%)",
                              } : {}),
                            }}
                          >
                            OTP
                          </Button>
                      </Stack>
                    )}

                    {loginMethod === "password" && authConfig.passwordAllowed && (
                      <Box>
                        <StyledTextField
                          fullWidth
                          size="small"
                          label="Password"
                          type={uiState.showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          value={form.password}
                          autoComplete="new-password"
                          onChange={(e) => updateForm("password", e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: "text.disabled", fontSize: 20 }} /></InputAdornment>,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => updateUi({ showPassword: !uiState.showPassword })} edge="end" size="small">
                                  {uiState.showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    )}

                    {loginMethod === "otp" && authConfig.otpAllowed && (
                      <Stack spacing={2.4}>
                        {otpState.requested && (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            OTP sent to {otpState.maskedPhone || "your mobile"}.
                          </Alert>
                        )}

                        {otpState.candidates.length > 1 && (
                          <Box>
                            <StyledTextField
                              select
                              fullWidth
                              size="small"
                              label="Student account"
                              SelectProps={{ displayEmpty: true }}
                              placeholder="Choose student account"
                              value={otpState.selectedStudentId}
                              onChange={(e) => updateOtpState({ selectedStudentId: e.target.value })}
                            >
                              <MenuItem value="">Choose student</MenuItem>
                              {otpState.candidates.map((candidate) => (
                                <MenuItem key={candidate.studentId} value={String(candidate.studentId)}>
                                  {candidate.studentName} ({candidate.admissionNumber})
                                </MenuItem>
                              ))}
                            </StyledTextField>
                          </Box>
                        )}

                        {otpState.requested && (
                          <Box>
                            <StyledTextField
                              fullWidth
                              size="small"
                              label="OTP"
                              placeholder="Enter OTP"
                              value={otpState.otp}
                              autoComplete="one-time-code"
                              onChange={(e) => updateOtpState({ otp: e.target.value })}
                            />
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Stack>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2.5, mb: 3.25 }}>
                    <FormControlLabel
                      control={<Checkbox checked={form.rememberMe} onChange={(e) => updateForm("rememberMe", e.target.checked)} size="small" />}
                      label={<Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary", fontSize: "0.88rem" }}>Remember me</Typography>}
                    />
                    <Typography 
                      variant="body2" 
                      fontWeight={600} 
                      color="primary" 
                      sx={{ fontSize: "0.88rem", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                      onClick={() => navigate(C.ROUTES.FORGOT_PASSWORD)}
                    >
                      {C.UI_TEXT.LINK_FORGOT_PASSWORD}
                    </Typography>
                  </Box>

                  {loginMethod === "password" || !authConfig.otpAllowed ? (
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={uiState.isLoading || authConfig.loading}
                      sx={{
                        py: 1.4,
                        borderRadius: 1.5,
                        fontWeight: 700,
                        fontSize: "0.98rem",
                        textTransform: "none",
                        boxShadow: "none",
                        background: "linear-gradient(135deg, #4f6df6 0%, #3b5bdb 100%)",
                        "&:hover": {
                          boxShadow: "none",
                          background: "linear-gradient(135deg, #4865ed 0%, #3251d4 100%)",
                        },
                      }}
                    >
                      {uiState.isLoading ? C.UI_TEXT.BUTTON_SIGNING_IN : C.UI_TEXT.BUTTON_SIGN_IN}
                    </Button>
                  ) : (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={uiState.isLoading || authConfig.loading}
                        onClick={handleSendOtp}
                        sx={{ py: 1.35, borderRadius: 1.5, fontWeight: 700, textTransform: "none", fontSize: "0.94rem" }}
                      >
                        {otpState.requested ? "Resend OTP" : "Send OTP"}
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={uiState.isLoading || !otpState.requested}
                        onClick={handleVerifyOtp}
                        sx={{
                          py: 1.3,
                          borderRadius: 1.5,
                          fontWeight: 700,
                          fontSize: "0.94rem",
                          textTransform: "none",
                          boxShadow: "none",
                          background: "linear-gradient(135deg, #4f6df6 0%, #3b5bdb 100%)",
                          "&:hover": {
                            boxShadow: "none",
                            background: "linear-gradient(135deg, #4865ed 0%, #3251d4 100%)",
                          },
                        }}
                      >
                        {uiState.isLoading ? "Verifying..." : "Verify OTP"}
                      </Button>
                    </Stack>
                  )}
                </Box>
                </Box>
              </AuthCard>
            </MainCanvas>
          </WorkspaceFrame>
        </Grow>

        <Snackbar
          open={!!uiState.message && uiState.messageType !== "error"}
          autoHideDuration={C.CONFIG.SNACKBAR_DURATION}
          onClose={() => updateUi({ message: "" })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => updateUi({ message: "" })} severity={uiState.messageType} variant="filled" sx={{ borderRadius: 2 }}>
            {uiState.message}
          </Alert>
        </Snackbar>
      </PageShell>
    </ThemeProvider>
  );
};

export default LoginPage;
